import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { AlertCircle } from './icons';

export interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  /** Keep the label for assistive tech only (when a visible group label already exists). */
  hideLabel?: boolean;
  hint?: string | undefined;
  error?: string | undefined;
  /** Rendered inside the control at the start (e.g. a dial-code prefix or an icon). */
  leading?: ReactNode;
  trailing?: ReactNode;
  /** Force LTR for phone numbers, emails and codes even in the Arabic UI. */
  ltr?: boolean;
}

/**
 * Labelled input with hint and inline error (aria-describedby wired). 48px tall, 16px text so
 * iOS never auto-zooms. The wrapper carries `.control`, which owns the single focus style.
 */
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  {
    label,
    hideLabel = false,
    hint,
    error,
    leading,
    trailing,
    ltr = false,
    className = '',
    id,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
      <label htmlFor={inputId} className={hideLabel ? 'sr-only' : 'text-sm font-medium text-ink'}>
        {label}
      </label>
      <div
        className={[
          'control flex h-12 items-center gap-2 rounded-md border border-line-strong bg-surface px-3',
          rest.disabled ? 'bg-surface-2 opacity-70' : '',
        ].join(' ')}
        dir={ltr ? 'ltr' : undefined}
      >
        {leading}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className={[
            'h-full min-w-0 flex-1 bg-transparent text-base text-ink placeholder:text-ink-3',
            ltr ? 'text-left' : '',
            className,
          ].join(' ')}
          {...rest}
        />
        {trailing}
      </div>
      {error ? (
        <p
          id={errorId}
          role="alert"
          className="flex items-start gap-1.5 text-sm text-critical-text"
        >
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-sm text-ink-2">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
